import * as React from 'react';
import {Box, Button, Stepper, Step, StepLabel, Grid, CircularProgress, Alert} from '@mui/material';
import {useNavigate} from 'react-router-dom'
import {useState, useRef} from "react";
import CheckIcon from '@mui/icons-material/CheckCircle';
import axios from "axios";
import dayjs from 'dayjs';
import {getLocalToken} from "../../utils/index";
import StepBasicInfo from './steps/basicInfo';
import AdditionalInsurance from "./steps/additionalInsurance";
import FamilyMembers from './steps/familyMembers';
import Plans from './steps/plans';
import MainUserInfo from './steps/mainUserInfo';
import PaidIcon from '@mui/icons-material/Paid';
import InfoIcon from '@mui/icons-material/Info';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const steps = [
    {label: 'Basisinformationen'},
    {label: 'Zusätzliche Optionen'},
    {label: 'Familienmitglieder'},
    {label: 'Wählen Sie den Plan'},
    {label: 'Benutzerinformationen'},
];

const stepIcons = {
    1: <InfoIcon sx={{ fontSize: 32 }}/>,
    2: <LocalHospitalIcon sx={{ fontSize: 32 }}/>,
    3: <FamilyRestroomIcon sx={{ fontSize: 32 }}/>,
    4: <PaidIcon sx={{ fontSize: 32 }}/>,
    5: <AccountCircleIcon sx={{ fontSize: 32 }} />
};

function CustomStepIcon(props) {
    const { active, completed, icon } = props;

    return (
        <Box
            sx={{
                color: active ? 'primary.main' : 'text.disabled',
                display: 'flex',
                height: 22,
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
            }}
        >
            {completed ? <CheckIcon color="success" sx={{ fontSize: 32 }} /> : stepIcons[icon]}
        </Box>
    );
}

export default function CreateNewUser({ prefilledUser = null, prefilledFamily = null }) {

    const plansRef = useRef();
    const token = getLocalToken();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [plans, setPlans] = useState([]);
    const [formData, setFormData] = useState({
        hmo: true,
        standard: true,
        familyDoctor: true,
        telemedicine: true,

        additionalInsurancePackage: '',
        additionalInsuranceCustomOption: [],

        dateOfBirth: dayjs(),
        gender: 'male',
        canton: null,
        regionId: 0,
        franchise: 300,
        accidentsCoverage: true,
        familyMembers: []
    });
    const [formErrors, setFormErrors] = useState({});
    const [zip, setZip] = useState('');
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [error, setError] = useState(null);
    const [regionsLoading, setRegionsLoading] = useState(false);
    const [isPlansStepValid, setIsPlansStepValid] = useState(true);
    const [selectedPlanOptions, setSelectedPlanOptions] = useState({});
    const [plansLoading, setPlansLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedInsuranceUid, setSelectedInsurerUid] = useState(null);

    React.useEffect(() => {
        if (prefilledUser) {
            setFormData(prev => ({
                ...prev,
                ...prefilledUser,
                dateOfBirth: prefilledUser?.dateOfBirth ? dayjs(prefilledUser.dateOfBirth) : dayjs(),
                gender: prefilledUser?.gender ? prefilledUser.gender : 'male',
                familyMembers: prefilledFamily?.length ? prefilledFamily : []
            }));
        }
    }, [prefilledUser, prefilledFamily]);

    const handleAdditionalSelectChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (name === 'additionalInsurancePackage') {
                return {
                    ...prev,
                    additionalInsurancePackage: value,
                    additionalInsuranceCustomOption:
                        value === 'custom' ? ['hospital'] : []
                };
            }
            return {
                ...prev,
                [name]: typeof value === 'string' ? value.split(',') : value
            };
        });
    };

    const handleNextStepClick = async () => {
        if (validateForm()) {
            // From step 2 to step 3, fetch offered plans
            if (activeStep === 2) {
                try {
                    setPlansLoading(true);
                    const response = await axios.post(
                        '/admin/api/v1/catalog/search',
                        formData,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    const offeredPlans = response.data.payload || [];
                    if(offeredPlans.length == 0){
                        setPlansLoading(false);
                        setError('No plans found on selected criteria. Please select another options.');
                        return;
                    }
                    setPlans(offeredPlans);
                    setPlansLoading(false);
                    setError('');
                } catch (err) {
                    setPlansLoading(false);
                    console.error('Error fetching offered plans:', err);
                    setError('Failed to get offered plans. Please try again.');
                    return;
                }
            }
            if (activeStep === 3) {
                const selected = plansRef.current.getSelectedPlanOptions();
                const { selectedInsurerUid, selectedOptions } = plansRef.current.getSelectedPlanOptions();
                if(selected.mainUser?.optionUID){
                    setSelectedInsurerUid(selected.mainUser?.optionUID)
                }
                if(formData.familyMembers.length > 0){
                    Object.entries(selected).forEach(([key, value]) => {
                        if (key.startsWith('familyMember_')) {
                            // Extract the index number from the key, e.g., 'familyMember_2' => 2
                            const index = parseInt(key.split('_')[1], 10);
                            if (formData.familyMembers[index]) {
                                formData.familyMembers[index].selectedPlan = value.optionUID;
                            }
                        }
                    });
                }
                if(!selected || !selected.mainUser || !selected.mainUser.optionUID || selected.mainUser.optionUID == ''){
                    alert("Bitte wählen Sie einen Versicherer für den Hauptnutzer aus!");
                    return;
                }
                const insurerUIDs = Object.values(selected).map(item => item.insurerUID);
                // Check if all insurerUIDs are the same
                const allSameInsurer = insurerUIDs.every(uid => uid === insurerUIDs[0]);
                if(!allSameInsurer){
                    alert("Alle Mitglieder müssen bei derselben Versicherung versichert sein!");
                    return;
                }
                // Check if all family members have plan selected
               if(!areAllFamilyMembersSelected(selected, formData.familyMembers)){
                   alert("Nicht alle Familienmitglieder haben einen Plan ausgewählt!");
                   return;
               }
            }
            setActiveStep((prev) => prev + 1);
        } else {
            console.log('Validation failed');
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    function areAllFamilyMembersSelected(selection, familyMembers) {
        if (!familyMembers || familyMembers.length === 0) {
            return true;
        }
        // Check all familyMember_X keys exist in selection
        for (let i = 0; i < familyMembers.length; i++) {
            if (
                !selection.hasOwnProperty(`familyMember_${i}`) ||
                !selection[`familyMember_${i}`] || // also check value exists
                !selection[`familyMember_${i}`].optionUID
            ) {
                // missing or invalid selection for a family member
                return false;
            }
        }
        return true;
    }
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleCheckBoxChange = (e) => {
        const {name, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleFamilyMembersChange = (updatedMembers) => {
        setFormData((prev) => ({
            ...prev,
            familyMembers: updatedMembers,
        }));
    };

    const handleZipInputChange = async (e) => {
        const newZip = e.target.value;
        if (!/^\d*$/.test(newZip)) {
            setFormErrors((prev) => ({
                ...prev,
                zip: 'ZIP must be exactly 4 digits'
            }));
        }
        if (newZip.length > 4) return;
        setZip(newZip);
        setFormData((prev) => ({
            ...prev,
            zip: newZip
        }));
        if (newZip.length !== 4) {
            setFormErrors((prev) => ({
                ...prev,
                zip: 'ZIP must be exactly 4 digits'
            }));
        } else {
            setFormErrors((prev) => {
                const {zip, ...rest} = prev;
                return rest;
            });
        }
        if (newZip.length === 4) {
            setRegionsLoading(true);
            try {
                const response = await axios.get(`/admin/api/v1/regions?postal=${newZip}`, {
                    headers: {Authorization: `Bearer ${token}`},
                });
                const data = response.data.payload || [];
                setRegions(data);
                setSelectedRegion(data[0]?.uid || '');
                setFormData((prev) => ({
                    ...prev,
                    zip: newZip,
                    region: data[0]?.uid || '',
                    regionId: data[0]?.regionId || 0,
                    canton: data[0]?.canton || null
                }));
            } catch (error) {
                console.error('Error fetching regions:', error);
                setRegions([]);
                setSelectedRegion('');
            } finally {
                setRegionsLoading(false);
            }
        } else {
            setRegions([]);
            setSelectedRegion('');
            setRegionsLoading(false);
        }
    };

    const handleRegionChange = (e) => {
        const region = regions.find(region => region.uid === e.target.value);
        const selected = e.target.value;
        setSelectedRegion(selected);
        setFormData(prev => ({
            ...prev,
            region: selected,
            canton: region.canton || null,
            regionId: region.regionId || 0
        }));
    };

    const handleSubmit = async () => {
        const payload = {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            password: formData.password,
            dateOfBirth: formData.dateOfBirth,
            familyMembers: formData.familyMembers,
            gender: formData.gender,
            phoneNumber: formData.phoneNumber,
            socialNumber: formData.socialNumber,
            selectedPlan: selectedInsuranceUid,
            city: formData.city,
            street: formData.street,
            houseNumber: formData.houseNumber,
            additionalServices: [],
            package: formData.additionalInsurancePackage,
            customOption: formData.additionalInsuranceCustomOption
        };
        if(!formData.firstName){
            setFormErrors(prev => ({
                ...prev,
                firstName: "Bitte geben Sie den Vornamen ein"
            }));
            return
        }
        if(!formData.lastName){
            setFormErrors(prev => ({
                ...prev,
                lastName: "Bitte geben Sie den Nachnamen ein"
            }));
            return
        }
        if(!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)){
            setFormErrors(prev => ({
                ...prev,
                email: "Bitte geben Sie eine gültige E-Mail-Adresse im format example@example.com ein"
            }));
            return
        }
        if(!formData.city){
            setFormErrors(prev => ({
                ...prev,
                city: "Bitte geben Sie cit einy"
            }));
            return
        }
        if (!formData.street || typeof formData.street !== 'string') {
            setFormErrors(prev => ({
                ...prev,
                street: "Bitte Straße eingeben."
            }));
            return;
        }
        if (!formData.street || typeof formData.street !== 'string') {
            setFormErrors(prev => ({
                ...prev,
                street: "Bitte Hausnummer eingeben."
            }));
            return;
        }
        const streetParts = formData.street.split(',').map(p => p.trim());
        setSubmitLoading(true);
        if(prefilledUser){
            try {
                axios.post(`/admin/api/v1/users/plans/${prefilledUser.uid}/update`, payload, {
                    headers: {Authorization: `Bearer ${token}`}
                })
                    .then(res => {
                        console.log(res)
                        if (res && res.status == 200 && !res.data.payload.error) {
                            navigate('/user-plans')
                        }
                    })
                    .catch(err => {
                        console.log(err);
                        setSubmitLoading(false);
                        if (err.response && err.response.data.error) {
                            setError(<Alert severity="error">Fehler beim Erstellen des Benutzers. {err.response.data.error.message}</Alert>)
                        } else {
                            setError(<Alert severity="error">Benutzer konnte nicht erstellt werden. Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut und/oder kontaktieren Sie den Support.</Alert>)
                        }
                    })
            } catch (err) {
                setSubmitLoading(false);
                console.log(err);
            }
        } else {
            try {
                axios.post('/admin/api/v1/users', payload, {
                    headers: {Authorization: `Bearer ${token}`}
                })
                    .then(res => {
                        console.log(res)
                        if (res && res.status == 200 && !res.data.payload.error) {
                            navigate('/user-plans')
                        }
                    })
                    .catch(err => {
                        console.log(err);
                        setSubmitLoading(false);
                        if (err.response && err.response.data.error) {
                            setError(<Alert severity="error">Fehler beim Erstellen des Benutzers. {err.response.data.error.message}</Alert>)
                        } else {
                            setError(<Alert severity="error">Benutzer konnte nicht erstellt werden. Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut und/oder kontaktieren Sie den Support.</Alert>)
                        }
                    })
            } catch (err) {
                setSubmitLoading(false);
                console.log(err);
            }
        }
    };

const validateForm = () => {
    const errors = {};
    if (!zip || zip.trim() === '') {
        errors.zip = 'ZIP code is required';
    }
    if (!selectedRegion) {
        errors.region = 'Region is required';
    }
    if (!formData.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is required';
    }
    if (formData.franchise == 'undefined' || formData.franchise == null) {
        errors.franchise = 'Franchise is required';
    }
    if (formData.accidentsCoverage === undefined || formData.accidentsCoverage === null) {
        errors.accidentsCoverage = 'Accidents Coverage must be specified';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
};

const validateFamilyMembersStep = () => {
    let valid = true;
    if(formData.familyMembers.length > 0){
        formData.familyMembers.forEach( member => {
            if(!member.firstName || !member.lastName){
                valid = false
            }
        })
    }
    return valid;
};

const isStepValid = () => {
    if (activeStep === 0) {
        return (
            zip.trim() !== '' &&
            selectedRegion &&
            formData.dateOfBirth &&
            (formData.franchise != 'undefined' || formData.franchise != null) &&
            formData.accidentsCoverage !== undefined &&
            formData.accidentsCoverage !== null
        );
    }
    if(activeStep === 2){
        return validateFamilyMembersStep();
    }
    if (activeStep === 3) {
        return isPlansStepValid;
    }
    return true;
};

const getStepContent = (step) => {
    switch (step) {
        case 0:
            return (
                <StepBasicInfo
                    formData={formData}
                    formErrors={formErrors}
                    zip={zip}
                    regions={regions}
                    selectedRegion={selectedRegion}
                    handleInputChange={handleInputChange}
                    handleZipInputChange={handleZipInputChange}
                    handleRegionChange={handleRegionChange}
                    handleAccidentsCoverageChange={handleCheckBoxChange}
                    regionsLoading={regionsLoading}
                />
            );
        case 1:
            return (
                <AdditionalInsurance
                    formData={formData}
                    formErrors={formErrors}
                    hmo={formData.hmo}
                    standard={formData.standard}
                    familyDoctor={formData.familyDoctor}
                    telemedicine={formData.telemedicine}
                    handleAdditionalInsuranceChange={handleCheckBoxChange}
                    handleSelectChange={handleAdditionalSelectChange}
                />
            );
        case 2:
            return (
                <FamilyMembers
                    familyMembers={formData.familyMembers}
                    onChange={handleFamilyMembersChange}
                />
            );
        case 3:
            return (
                <Plans
                    plans={plans}
                    ref={plansRef}
                    familyMember={formData.familyMembers}
                    nextBtnAction={handleNextStepClick}
                    backBtnAction={handleBack}
                />
            );
        case 4:
            return (
                <MainUserInfo
                    formData={formData}
                    formErrors={formErrors}
                    handleInputChange={handleInputChange}
                    showPassword={!prefilledUser}
                />
            );
        default:
            return 'Unknown step';
    }
};

let nextBtnLabel;
switch (activeStep) {
    case 4:
        nextBtnLabel = 'Benutzer erstellen';
        break;
    case 2:
        nextBtnLabel = 'Suchplan';
        break;
    default:
        nextBtnLabel = 'Nächste';
        break;
}

return (
    <Grid container spacing={1} sx={{padding: 1}}>
        <Box sx={{width: '100%'}}>
            <Stepper activeStep={activeStep}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel StepIconComponent={CustomStepIcon}>
                            <span>{step.label}</span>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{mt: 2}}>{getStepContent(activeStep)}</Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    pt: 2,
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Button color="inherit" variant="outlined" disabled={activeStep === 0} onClick={handleBack}>
                    Zurück
                </Button>

                <Box sx={{flex: 1}}/>
                <Button variant="outlined"
                    onClick={activeStep === steps.length - 1 ? handleSubmit : handleNextStepClick}
                    disabled={!isStepValid() || plansLoading}
                    startIcon={plansLoading || submitLoading ? <CircularProgress size={20}/> : null}
                >
                    <span> {nextBtnLabel}</span>
                </Button>
            </Box>

            <span>{error && <Box sx={{mt: 2, color: 'red'}}><span>{error}</span></Box>}</span>
        </Box>
    </Grid>
);

}